import { getEase, Ease, Tween } from "@westpierstudio/luma/createjs";
import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { Signal } from "type-signals";
import { Spine } from "pixi-spine";
import { BoundedContainer, ImplementBoundedContainer } from "utils/BoundedContainer";
import { Container, Sprite, Text } from "pixi.js";
import { minimumHeightSpecSelector } from "utils/Responsive";

class VSResultPanelSignals {
    moveStart = new Signal();
    showComplete = new Signal();
    tearStart = new Signal();
    winSounds = new Signal();
    panelIn = new Signal();
}

const NO_TINT = 0xFFFFFF;

export class VSResultPanel extends View {
    private panelContainer: Container;
    private panel: Spine & Container;
    private panelContainerMovePositions!: {
        outX: number;
        outY: number;
        bounceX: number;
        bounceY: number;
        inX: number;
        inY: number;
    };
    private infoContainer: Container;
    private infoMovePositions: Record<string, any>;
    private logo: Sprite;
    private text: {winner: Text, draw: Text, value: Text};
    private tintables: {tint: number}[] = [];
    private darkenTint: number;
    private tear: Spine & Container;
    private tearAnimation: string;
    private team: string;
    private spec: Record<string, any>;
    private data: {state: string, win: number, picked: boolean};
    signals = new VSResultPanelSignals();
    valueBounds: BoundedContainer;

    constructor(protected override readonly services: GameServices, specKey: string) {
        super(services);

        const {resources, factory} = services;

        this.team = specKey;

        this.spec = resources.getSpec(`vsresultpanels`);
        factory.applySpec(this, this.spec[specKey]);

        this.panelContainer = factory.createContainer({}, this);

        if (specKey == `dragons`) {
            this.panel = factory.createSpine(this.spec.dragonResultPanel, this.panelContainer);
        } else {
            this.panel = factory.createSpine(this.spec.pandaResultPanel, this.panelContainer);
        }

        this.infoContainer = factory.createContainer({}, this.panelContainer);
        this.infoMovePositions = this.spec.infoMovePositions[specKey];

        this.valueBounds = new BoundedContainer(this.spec.valueBounds);
        factory.applySpec(this.valueBounds, this.spec.valueBounds);
        this.infoContainer.addChild(this.valueBounds);
        this.text = {
            winner: factory.createText(resources.getMessage(`vsWinner`), this.spec.vsWinner, this.infoContainer),
            draw: factory.createText(resources.getMessage(`vsDraw`), this.spec.vsDraw, this.infoContainer),
            value: factory.createText(``, this.spec.vsValue, this.valueBounds)
        };
        ImplementBoundedContainer(this.text.winner, this.spec.titleBounds).update();
        ImplementBoundedContainer(this.text.draw, this.spec.titleBounds).update();

        if (specKey == `dragons`) {
            this.logo = factory.createSprite(this.spec.dragonLogo, this.infoContainer);
            this.text.winner.x = this.spec.dragonInfoX;
            this.text.draw.x = this.spec.dragonInfoX;
            this.logo.x = this.spec.dragonInfoX;
            this.text.value.x = this.spec.dragonInfoX;
        } else {
            this.logo = factory.createSprite(this.spec.pandaLogo, this.infoContainer);
            this.text.winner.x = this.spec.pandaInfoX;
            this.text.draw.x = this.spec.pandaInfoX;
            this.logo.x = this.spec.pandaInfoX;
            this.text.value.x = this.spec.pandaInfoX;
        }

        this.data = {
            state: `none`,
            win: 0,
            picked: true,
        };

        this.tintables.push(this.panel);
        this.tintables.push(this.text.winner);
        this.tintables.push(this.text.draw);
        this.tintables.push(this.text.value);
        this.tintables.push(this.logo);

        this.darkenTint = parseInt(this.spec.darkenTint);

        if (specKey == `dragons`) {
            this.tear = factory.createSpine(this.spec.dragonTear, this);
            this.tearAnimation = `dragon-tear`;
        } else {
            this.tear = factory.createSpine(this.spec.pandaTear, this);
            this.tearAnimation = `panda-tear`;
        }

        this.visible = false;
        this.panel.visible = false;
        this.text.winner.visible = false;
        this.text.draw.visible = false;
        this.text.value.visible = false;
        this.logo.visible = false;

        this.resize();
        services.layout.onResize.add(()=>this.resize());
    }

    private calculatePanelPositions(): void {

        const bounds = this.services.layout.getScreenBoundaryInBucketSpace();
        const {left, right, bottom} = bounds;
        const offsetX = 70;
        const offsetY = 10;

        if (this.team === `dragons`) {
            this.panelContainerMovePositions = {
                outX: left-this.panel.x-this.x-offsetX,
                outY: bottom-this.panel.y-this.y+offsetY,

                bounceX: 5,
                bounceY: -5,

                inX: 0,
                inY: 0,
            };
        } else {
            this.panelContainerMovePositions = {
                outX: right-this.panel.x-this.x+offsetX,
                outY: bottom-this.panel.y-this.y+offsetY,

                bounceX: -5,
                bounceY: -5,

                inX: 0,
                inY: 0,
            };
        }
    }

    showWin(win: number, picked: boolean): void {

        this.data = {
            state: `win`,
            win: win,
            picked: picked,
        };

        const {tearHoldTime} = this.services.resources.getSetting(`vsResultPanel`);

        this.visible = true;
        this.tear.visible = true;

        this.signals.tearStart.dispatch();
        this.tear.state.setAnimation(0, this.tearAnimation, false);

        Tween.get(this)
        .wait(tearHoldTime)
        .call(() => {
            this.signals.winSounds.dispatch();
            this.signals.panelIn.dispatch();
            this.showWinText();
        });
    }

    showWinText(skip: boolean=false): void {

        this.text.winner.visible = true;
        this.text.value.text = `` + this.services.currency.formatCoins(this.data.win, {group: true});;

        this.setTint((this.data.picked) ? NO_TINT : this.darkenTint);

        this.text.value.visible = true;
        this.logo.visible = true;

        this.valueBounds.update();

        if (!skip) { this.show(`win`); }
    }

    showLoss(win: number, picked: boolean): void {

        this.data = {
            state: `loss`,
            win: win,
            picked: picked,
        };

        const {tearHoldTime} = this.services.resources.getSetting(`vsResultPanel`);

        this.visible = true;
        this.tear.visible = true;

        Tween.get(this)
        .wait(tearHoldTime)
        .call(() => {
            this.signals.panelIn.dispatch();
            this.showLossText();
        });
    }

    showLossText(skip: boolean=false): void {

        this.text.value.text = `` + this.services.currency.formatCoins(this.data.win, {group: true});;

        this.setTint((this.data.picked) ? NO_TINT : this.darkenTint);

        this.text.value.visible = true;
        this.logo.visible = true;

        this.valueBounds.update();

        if (!skip) { this.show(`loss`); }
    }

    showDraw(win: number): void {

        this.data = {
            state: `draw`,
            win: win,
            picked: true,
        };

        this.visible = true;

        Tween.get(this).call(() => {
            this.signals.panelIn.dispatch();
            this.showDrawText();
        });
    }

    showDrawText(skip: boolean=false): void {

        this.text.draw.visible = true;
        this.text.value.text = `` + this.services.currency.formatCoins(this.data.win, {group: true});

        this.setTint(NO_TINT);

        this.text.value.visible = true;
        this.logo.visible = true;

        this.valueBounds.update();

        if (!skip) { this.show(`draw`); }
    }

    hide(): void {
        this.visible = false;
        this.panel.visible = false;
        this.text.winner.visible = false;
        this.text.draw.visible = false;
        this.text.value.visible = false;
        this.logo.visible = false;
    }

    override reset(): void {

        Tween.removeTweens(this);
        Tween.removeTweens(this.infoContainer);

        this.infoContainer.x = 0;
        this.infoContainer.y = 0;

        this.visible = false;
    }

    private setTint(tint: number): void {

        for (const item of this.tintables) {
            item.tint = tint;
        }
    }

    private show(type: string): void {

        const drawMoveTime: number = this.services.resources.getSetting(`vsResultPanel`).drawMoveTime;
        const drawSettleTime: number = this.services.resources.getSetting(`vsResultPanel`).drawSettleTime;
        let {holdTime} = this.services.resources.getSetting(`vsResultPanel`);

        this.infoContainer.x = 0;
        this.infoContainer.y = 0;

        this.panel.visible = true;
        this.infoContainer.scale.x = 1;
        this.infoContainer.scale.y = 1;

        if (this.team === `dragons`) {
            this.panel.state.setAnimation(0, `Dragon_static`, false);
        } else {
            this.panel.state.setAnimation(0, `Panda_Static`, false);
        }

        if (type === `draw`) {
            this.calculatePanelPositions();

            this.panelContainer.x = this.panelContainerMovePositions.outX;
            this.panelContainer.y = this.panelContainerMovePositions.outY;
        }

        if (this.team === `dragons`) {
            if (type === `win`) {
                this.panel.state.setAnimation(0, `Dragon_win`, false);
            } else if (type === `loss`) {
                this.panel.state.setAnimation(0, `Dragon_loss`, false);
            } else {
                holdTime += drawMoveTime + drawSettleTime;

                Tween.get(this.panelContainer).to({x: this.panelContainerMovePositions.bounceX, y: this.panelContainerMovePositions.bounceY}, drawMoveTime, Ease.quadOut)
                .to({x: this.panelContainerMovePositions.inX, y: this.panelContainerMovePositions.inY}, drawSettleTime, Ease.quadInOut)
                .call(() => {
                    this.panel.state.setAnimation(0, `Dragon_draw`, false);
                });
            }
        } else {
            if (type === `win`) {
                this.panel.state.setAnimation(0, `Panda_Win`, false);
            } else if (type === `loss`) {
                this.panel.state.setAnimation(0, `Panda_Loss`, false);
            } else {
                holdTime += drawMoveTime + drawSettleTime;

                Tween.get(this.panelContainer).to({x: this.panelContainerMovePositions.bounceX, y: this.panelContainerMovePositions.bounceY}, drawMoveTime, Ease.quadOut)
                .to({x: this.panelContainerMovePositions.inX, y: this.panelContainerMovePositions.inY}, drawSettleTime, Ease.quadInOut)
                .call(() => {
                    this.panel.state.setAnimation(0, `panda-draw`, false);
                });
            }
        }

        Tween.get(this)
        .wait(holdTime)
        .call(() => {

            this.startMove();
            this.signals.moveStart.dispatch();
        });
    }

    private startMove(): void {

        const {part1Time, part2Time, part1Ease, part2Ease, hold2Time} = this.services.resources.getSetting(`vsResultPanel`).infoMove;

        Tween.get(this.infoContainer.scale).to({x: 0.65, y: 0.65}, part2Time);

        Tween.get(this.infoContainer)
        .to(this.infoMovePositions.one, part1Time, getEase(part1Ease))
        .call(() => {

            Tween.get(this.infoContainer)
            .to(this.infoMovePositions.two, part2Time, getEase(part2Ease))
            .wait(hold2Time)
            .call(() => {
                this.panel.visible = false;
                this.signals.showComplete.dispatch();
            });
        });
    }

    skip(): void {
        Tween.removeTweens(this);
        Tween.removeTweens(this.infoContainer);
        this.panel.visible = false;
        this.tear.visible = false;

        if (this.data.state === `win`) {
            this.showWinText(true);
        } else if (this.data.state === `loss`) {
            this.showLossText(true);
        } else {
            this.showDrawText(true);
        }

        this.signals.moveStart.dispatch();
        this.signals.showComplete.dispatch();

        this.infoContainer.scale.x = 0.65;
        this.infoContainer.scale.y = 0.65;
        this.infoContainer.x = this.infoMovePositions.two.x;
        this.infoContainer.y = this.infoMovePositions.two.y;
        this.infoContainer.visible = true;
    }

    resize() {
        const portraitSpec = minimumHeightSpecSelector(this.services, this.spec.portrait);
        this.services.factory.applySpec(this, portraitSpec);
    }
}